// The claim statuses as people see them (David, 2026-10-21). Atlas keeps every status it
// stores; these are the simpler names shown on the Status filter and the status badges:
//   Paid      = paid and credit (an overpaid claim)
//   Rejected  = rejected and appealed
//   Submitted = submitted and partial (insurance paid part; the rest is still open)
// Each group lists its stored statuses with the one it is named after first. Alphabetical,
// the order the Status filter lists them in.
export const STATUS_GROUPS = [
  { key: "accepted", label: "Accepted", statuses: ["accepted"] },
  { key: "denied", label: "Denied", statuses: ["denied"] },
  { key: "draft", label: "Draft", statuses: ["draft"] },
  { key: "paid", label: "Paid", statuses: ["paid", "credit"] },
  { key: "patient", label: "Patient", statuses: ["patient"] },
  { key: "rebilled", label: "Rebilled", statuses: ["rebilled"] },
  { key: "rejected", label: "Rejected", statuses: ["rejected", "appealed"] },
  { key: "submitted", label: "Submitted", statuses: ["submitted", "partial"] },
];

// The client view folds Accepted into Submitted as well (David, 2026-10-21): to a clinic,
// "the clearinghouse accepted it" and "it was sent" are the same news. Staff keep both.
const CLIENT_STATUS_GROUPS = STATUS_GROUPS.filter((g) => g.key !== "accepted").map((g) =>
  g.key === "submitted" ? { ...g, statuses: ["submitted", "accepted", "partial"] } : g,
);

/** The groups for a viewer: the client view's, or staff's. */
export function statusGroups(clientView = false) {
  return clientView ? CLIENT_STATUS_GROUPS : STATUS_GROUPS;
}

function groupOf(status, clientView) {
  const raw = (status || "").trim().toLowerCase();
  return { raw, group: statusGroups(clientView).find((g) => g.statuses.includes(raw)) };
}

/** The group key a stored status shows as ("credit" -> "paid"); an unknown status as itself. */
export function displayStatusKey(status, clientView = false) {
  const { raw, group } = groupOf(status, clientView);
  return group ? group.key : raw;
}

/** The name a stored status shows as ("appealed" -> "Rejected"). */
export function displayStatus(status, clientView = false) {
  const { raw, group } = groupOf(status, clientView);
  if (group) return group.label;
  return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : "";
}

// A self-pay claim the self-pay rule closed with nothing owed (claim_source_fields,
// 2026-10-29): the PM still says "patient", the page says "Self-pay, closed".
export const SELF_PAY_CLOSED = "self_pay_closed";
export const SELF_PAY_CLOSED_LABEL = "Self-pay, closed";

/** The badge key for a claim row (its `self_pay_closed` flag, else its status). */
export function claimStatusKey(claim, clientView = false) {
  if (claim && claim.self_pay_closed) return SELF_PAY_CLOSED;
  return displayStatusKey(claim && claim.claim_status, clientView);
}

/** The badge text for a claim row: "Self-pay, closed", else the status key. */
export function claimStatusText(claim, clientView = false) {
  const key = claimStatusKey(claim, clientView);
  return key === SELF_PAY_CLOSED ? SELF_PAY_CLOSED_LABEL : key;
}

/** The badge colour for a status key, as the Claims tab draws it. Shared so any other
 * place that shows a claim's status (the Waterfall pop-up) draws the same badge. */
export function statusColor(status) {
  const colors = {
    draft: "#718096",
    submitted: "#2D4470",
    accepted: "#2D4470",
    paid: "#2E9B6B",
    partial: "#D4AD5C",
    denied: "#C53030",
    rejected: "#C53030",
    appealed: "#5B8A9A",
    rebilled: "#8A8F98",
    patient: "#C4973B",
    self_pay_closed: "#8A8F98",
  };
  return colors[status] || "#718096";
}
